import store from '@/store/index'
import router from '@/router/index'
import apiConfig from '@/api/api'
import requestConfig from '@/mixins/use-api-config'
import { userInfoDecrypted } from '@/utils/useTokenDecrypt'
import MerchantPortalAuthenticator from 'xpress_merchant_authenticator'
import { getMerchantPortalUrl } from '@/utils/appConfig'

const { api } = apiConfig()

// api request header configuration
const { config } = requestConfig()

const merchantPortalUrl = getMerchantPortalUrl()

const users = {
  namespaced: true,

  state: () => ({
    tokenType: sessionStorage.getItem('#'),
    userAuthToken: sessionStorage.getItem('##'),
    apiRequestToken: sessionStorage.getItem('###'),
    user: JSON.parse(sessionStorage.getItem('####')),
    // ---------------------------------------------
    adminUser: null,
    // ---------------------------------------------
    adminUsers: [],
    activatedAdminUsers: [],
    deactivatedAdminUsers: [],
    // ---------------------------------------------
    allMerchantUsers: [],
    activatedMerchantUsers: [],
    deactivatedMerchantUsers: [],
    // ---------------------------------------------
    allTransactionRepostMerchantUsers: [],
    pendingTransactionRepostMerchantUsers: [],
    approvedTransactionRepostMerchantUsers: [],
    deactivatedTransactionRepostMerchantUsers: [],
    transactionRepostMerchantUser: null
  }),

  getters: {
    _tokenType: state => state.tokenType,
    _userAuthToken: state => state.userAuthToken,
    _apiRequestToken: state => state.apiRequestToken,
    _user: state => state.user,
    // ---------------------------------------------
    _adminUser: (state) => state.adminUser,
    // ---------------------------------------------
    _adminUsers: (state) => state.adminUsers,
    _activatedAdminUsers: state => state.activatedAdminUsers,
    _deactivatedAdminUsers: state => state.deactivatedAdminUsers,
    // ---------------------------------------------
    _allMerchantUsers: state => state.allMerchantUsers,
    _activatedMerchantUsers: state => state.activatedMerchantUsers,
    _deactivatedMerchantUsers: state => state.deactivatedMerchantUsers,
    // ---------------------------------------------
    _allTransactionRepostMerchantUsers: state => state.allTransactionRepostMerchantUsers,
    _pendingTransactionRepostMerchantUsers: state => state.pendingTransactionRepostMerchantUsers,
    _approvedTransactionRepostMerchantUsers: state => state.approvedTransactionRepostMerchantUsers,
    _deactivatedTransactionRepostMerchantUsers: state => state.deactivatedTransactionRepostMerchantUsers,
    _transactionRepostMerchantUser: state => state.transactionRepostMerchantUser
  },

  mutations: {
    SAVE_TOKEN_TYPE (state, value) {
      sessionStorage.setItem('#', value)
    },
    SAVE_USER_AUTH_TOKEN (state, token) {
      sessionStorage.setItem('##', token)
    },
    SAVE_API_REQUEST_TOKEN (state, token) {
      sessionStorage.setItem('###', token)
    },
    SAVE_USER_INFO (state, data) {
      sessionStorage.setItem('####', data)
    },
    LOGOUT (state, token) {
      state.token = token
    },
    // -------------------------------------
    ADMIN_USER (state, user) {
      state.adminUser = user
    },
    // -------------------------------------
    ADMIN_USERS (state, users) {
      state.adminUsers = users
    },
    ACTIVATED_ADMIN_USERS (state, data) {
      state.activatedAdminUsers = data
    },
    DEACTIVATED_ADMIN_USERS (state, data) {
      state.deactivatedAdminUsers = data
    },
    // ------------------------------------
    ALL_MERCHANT_USERS (state, data) {
      state.allMerchantUsers = data
    },
    ACTIVATED_MERCHANT_USERS (state, data) {
      state.activatedMerchantUsers = data
    },
    DEACTIVATED_MERCHANT_USERS (state, data) {
      state.deactivatedMerchantUsers = data
    },
    // ------------------------------------
    ALL_TRANSACTION_REPOST_MERCHANT_USERS (state, data) {
      state.allTransactionRepostMerchantUsers = data
    },
    PENDING_TRANSACTION_REPOST_MERCHANT_USERS (state, data) {
      state.pendingTransactionRepostMerchantUsers = data
    },
    APPROVED_TRANSACTION_REPOST_MERCHANT_USERS (state, data) {
      state.approvedTransactionRepostMerchantUsers = data
    },
    DEACTIVATED_TRANSACTION_REPOST_MERCHANT_USERS (state, data) {
      state.deactivatedTransactionRepostMerchantUsers = data
    },
    TRANSACTION_REPOST_MERCHANT_USER (state, data) {
      state.transactionRepostMerchantUser = data
    }
  },

  actions: {
    RetrievePassword ({ rootState, commit }, { headers, email }) {
      return new Promise((resolve, reject) => {
        rootState.loading = true

        api.post(`/User/RetrievePassword?EmailAddress=${email}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(data => {
            rootState.loading = false
            resolve(data)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    ChangePassword ({ rootState, commit }, { headers, data }) {
      return new Promise((resolve, reject) => {
        rootState.loading = true

        api.post('/User/ChangePassword', data, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(data => {
            rootState.loading = false
            resolve(data)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    UserLogin () {
      // Define window dimensions
      const width = 800
      const height = 800

      // Calculate center position
      const left = (screen.width - width) / 2
      const top = (screen.height - height) / 2

      // Window features with calculated position
      const windowFeatures = [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'resizable=yes',
        'scrollbars=yes',
        'toolbar=no',
        'menubar=no',
        'location=no',
        'status=no'
      ].join(',')
      window.open(
        `${merchantPortalUrl}/login?rdt=true`,
        'centeredPopup',
        windowFeatures
      )
    },

    ValidateEmail ({ rootState }, token) {
      return new Promise((resolve, reject) => {
        rootState.loading = true
        api.post(`/User/ValidateEmail?Token=${token}`)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              return response.data
            }
          })
          .then(data => {
            // rootState.loading = false
            resolve(data)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    ValidateUser ({ rootState, commit }, config) {
      return new Promise((resolve, reject) => {
        rootState.loading = true
        api.get('/User/ValidateUser', config)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              return response.data
            }
          })
          .then(response => {
            // success response
            if ([0, '0', '00'].includes(response?.responseCode)) {
              const user = response.data
              // const { merchantId, isSelfOnboarding } = user
              const { merchantId } = user

              const userInfo = {
                name: `${user.firstName} ${user.lastName}`,
                email: user.emailAddress
              }

              const saveValidUserData = () => {
                // save user info
                commit('SAVE_USER_INFO', JSON.stringify(userInfo))
              }

              // CHECK USER AUTHENTICITY BEFORE LOGIN APPROVAL

              // if (!merchantId && isSelfOnboarding) {
              if (!merchantId) {
                // Invalid merchants, hence they're redirected to registration page
                reject(response)
              // } else if (merchantId && isSelfOnboarding) {
              } else if (merchantId) {
                // Valid merchant, hence redirected to merchant dashboard
                userInfo.merchantId = user.merchantId
                userInfo.role = 'Merchant'

                // save user data
                saveValidUserData()
                // user resolved
                resolve(response)
              } else {
                // Valid admin, hence redirected to admin dashboard
                userInfo.role = 'Administrator'

                // save user data
                saveValidUserData()
                // user resolved
                resolve(response)
              }

              // rootState.loading = false
            } else {
              resolve(response)
            }

            // stop spinner
            // rootState.loading = false
          })
          .catch(error => {
            if ([401, '401'].includes(error?.request?.status)) {
              sessionStorage.clear()
              // ---------------------------
              commit('LOGOUT', null)
              // ---------------------------
              rootState.loading = false
              // ---------------------------

              // user token expired, hence redirect to login
              location.assign('/login')
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    async AuthenticateUser ({ rootState, commit }, payload) {
      return new Promise((resolve, reject) => {
        // rootState.loading = true

        api.post('/User/AuthenticationUser', payload)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              return response.data
            }
          })
          .then(response => {
            // success response
            if ([0, '0', '00'].includes(response?.responseCode)) {
              // save user authentication token
              const accessTokenResponseData = response.data

              const userData_ = userInfoDecrypted(accessTokenResponseData)
              const user = userData_?.data

              // const { merchantId, isSelfOnboarding } = user
              const { merchantId } = user

              const userInfo = { ...user }

              const saveValidUserData = () => {
                // save user authentication token
                const userAuthToken = payload.ssoToken
                commit('SAVE_USER_AUTH_TOKEN', userAuthToken)

                // save access token
                commit('SAVE_TOKEN_TYPE', 'bearer')
                commit('SAVE_API_REQUEST_TOKEN', accessTokenResponseData)

                // save user info
                commit('SAVE_USER_INFO', JSON.stringify(userInfo))
              }

              // CHECK USER AUTHENTICITY BEFORE LOGIN APPROVAL

              // Valid merchant, hence redirected to merchant dashboard
              if (merchantId) {
                userInfo.role = 'Merchant'

                // save user data
                saveValidUserData()
                // user resolved
                resolve(response)
              } else {
                // Valid admin, hence redirected to admin dashboard
                userInfo.role = 'Administrator'

                // save user data
                saveValidUserData()
                // user resolved
                resolve(response)
              }

              // Invalid merchant/admin users, hence they're redirected to registration page
              reject(response)
            } else
            // token expired(unauthorized user)
            if (response?.responseCode === 4 || response?.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              // non payout user(fetch non-payout user detail, and redirect to registration page)
              const url = {
                name: 'User Registration'
              }

              const config = {
                headers: {}
              }

              store.dispatch('GetNonAppUserDetails', config)
                .then(response => {
                  // stop spinner
                  rootState.loading = false

                  // update form inputs
                  if ([0, '0', '00'].includes(response?.responseCode)) {
                    url.query.user = JSON.stringify(response.data)
                  }

                  // route to registration page
                  router.push(url)
                })
                .catch(() => {
                  // stop spinner
                  rootState.loading = false

                  // route to registration page
                  router.push(url)
                })
            }
          })
          .catch(error => {
            // rootState.loading = false
            if (error?.response?.status === 401) {
              store.dispatch('UserLogin')
            } else {
              // rootState.loading = false
              reject(error)
            }
          })
      })
    },
    async UnauthorizedUser ({ state }) {
      // sign out current user
      await api.post('/User/Logout', { email: state?.user?.emaillAddress }, config)
        .finally(() => {
          // clear local storage
          sessionStorage.clear()
          // route user to home page
          window.location.assign('/')
          // prompt user to login again
          store.dispatch('UserLogin')
        })
    },

    Logout ({ commit, rootState, state }, configHeaders) {
      return new Promise((resolve) => {
        rootState.loading = true

        // authenticator logout

        MerchantPortalAuthenticator.logOut({
          appKey: merchantPortalUrl,
          onError: (error) => console.error(error)
        })
          .then(() => {
            // payout logout

            Promise.resolve(api.post('/User/Logout', { email: state?.user?.emaillAddress }, configHeaders))
              .then(() => {
                sessionStorage.clear()
                commit('LOGOUT', null)

                resolve(true)
              })
              .catch(() => resolve(false))
          })
          .finally(() => {
            rootState.loading = false
          })
      })
    },

    GetAllUserNotXpress ({ commit, rootState }) {
      return new Promise((resolve, reject) => {
        api
          .get('/User/GetAllUserNotXpress', config)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              return response.data
            }
          })
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    GetAllUserNotInDepartment ({ commit, rootState }) {
      const headers = { Authorization: `${rootState.users.tokenType} ${rootState.users.apiRequestToken}` }

      return new Promise((resolve, reject) => {
        api
          .get('/User/GetAllUserNotInDepartment', { headers })
          .then(response => response)
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    GetNonAppUserDetails ({ rootState }, config) {
      return new Promise((resolve, reject) => {
        // rootState.loading = true
        api.get('/User/GetNonAppUserDetails', config)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              return response.data
            }
          })
          .then(response => {
            // rootState.loading = false
            resolve(response)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    // ------------------------------------------------
    GetAdminUserByEmail ({ rootState }, { headers, adminEmail }) {
      return new Promise((resolve, reject) => {
        api.get(`/User/GetUserByEmail?EmailAddress=${adminEmail}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(adminUsers => {
            resolve(adminUsers)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    // ------------------------------------------------

    // Admin Related Endpoints
    CreateUser ({ rootState }, { headers, data }) {
      rootState.loading = true

      return new Promise((resolve, reject) => {
        api.post('/User/CreateUser', data, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(response => {
            setTimeout(() => {
              rootState.loading = false
              resolve(response)
            }, 2000)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },

    ApproveUser ({ rootState }, { headers, data: { userId, comment } }) {
      return new Promise((resolve, reject) => {
        rootState.loading = true
        api.get(`/User/ApproveUser?UserId=${userId}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(data => {
            setTimeout(() => {
              rootState.loading = false
              resolve(data)
            }, 2500)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },

    ActivateAdminUser ({ rootState }, { headers, data: { userId, comment } }) {
      return new Promise((resolve, reject) => {
        rootState.loading = true
        api.get(`/User/ActivateAdminUser?UserId=${userId}&Comment=${comment}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(data => {
            setTimeout(() => {
              rootState.loading = false
              resolve(data)
            }, 2500)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    DeactivateAdminUser ({ rootState }, { headers, data: { userId, comment } }) {
      return new Promise((resolve, reject) => {
        rootState.loading = true
        api.get(`/User/DeactivateAdminUser?UserId=${userId}&Comment=${comment}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(data => {
            setTimeout(() => {
              rootState.loading = false
              resolve(data)
            }, 2500)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },

    GetAllAdminUsers ({ rootState, commit }, payload) {
      return new Promise((resolve, reject) => {
        api
          .get('/User/GetAllUserXpress', payload)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              return response.data
            }
          })
          .then(adminUsers => {
            resolve(adminUsers)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    GetActivatedAdminUsers ({ rootState, commit }, payload) {
      return new Promise((resolve, reject) => {
        api
          .get('/User/GetActivatedUserXpress', payload)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              return response.data
            }
          })
          .then(adminUsers => {
            resolve(adminUsers)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    GetDeactivatedAdminUsers ({ rootState, commit }, payload) {
      return new Promise((resolve, reject) => {
        api
          .get('/User/GetDeactivatedUserXpress', payload)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              return response.data
            }
          })
          .then(adminUsers => {
            resolve(adminUsers)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    // ------------------------------------------------------------

    // Merchant Related Endpoints
    CreateMerchantUser ({ rootState }, { headers, data }) {
      rootState.loading = true

      return new Promise((resolve, reject) => {
        api.post('/User/CreateMerchantUser', data, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(response => {
            setTimeout(() => {
              rootState.loading = false
              resolve(response)
            }, 2000)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },

    ApproveMerchantUser ({ rootState }, { headers, data: { userId, comment } }) {
      return new Promise((resolve, reject) => {
        rootState.loading = true
        api.get(`/User/ActivateAdminUser?UserId=${userId}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(data => {
            setTimeout(() => {
              rootState.loading = false
              resolve(data)
            }, 2500)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },

    ActivateMerchantUser ({ rootState }, { headers, data: { userId, comment } }) {
      return new Promise((resolve, reject) => {
        rootState.loading = true
        api.get(`/User/ActivateMerchantUser?UserId=${userId}&Comment=${comment}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(data => {
            setTimeout(() => {
              rootState.loading = false
              resolve(data)
            }, 2500)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    DeactivateMerchantUser ({ rootState }, { headers, data: { userId, comment } }) {
      return new Promise((resolve, reject) => {
        rootState.loading = true
        api.get(`/User/DeactivateMerchantUser?UserId=${userId}&Comment=${comment}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(data => {
            setTimeout(() => {
              rootState.loading = false
              resolve(data)
            }, 2500)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },

    // -----------------------------------------------
    GetAllMerchantUsers ({ rootState }, { headers, merchantId }) {
      return new Promise((resolve, reject) => {
        // rootState.loading = true
        api.get(`/User/GetAllMerchantUser?MerchantId=${merchantId}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(response => {
            // rootState.loading = false
            resolve(response)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    GetActivatedMerchantUsers ({ rootState }, { headers, merchantId }) {
      return new Promise((resolve, reject) => {
        // rootState.loading = true
        api.get(`/User/GetActivatedMerchantUser?MerchantId=${merchantId}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(response => {
            // rootState.loading = false
            resolve(response)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },
    GetDeactivatedMerchantUsers ({ rootState }, { headers, merchantId }) {
      return new Promise((resolve, reject) => {
        // rootState.loading = true
        api.get(`/User/GetDeactivatedMerchantUser?MerchantId=${merchantId}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              return response.data
            }
          })
          .then(response => {
            // rootState.loading = false
            resolve(response)
          })
          .catch(error => {
            // rootState.loading = false
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              rootState.loading = false
              reject(error)
            }
          })
      })
    },

    // merchant transaction repost
    CreateFundRespostUser ({ rootState }, { headers, data: { userId, privilegeId } }) {
      return new Promise((resolve, reject) => {
        return api.post(`/User/CreateFundRespostUser?UserId=${userId}&PrivilegeId=${privilegeId}`, null, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              reject(error)
            }
          })
      })
    },
    ApproveFundRespostUser ({ rootState }, { headers, data: { fundDisbursementRequeryUserId } }) {
      return new Promise((resolve, reject) => {
        return api.post(`/User/ApproveFundRespostUser?FundDisbursementRequeryUserId=${fundDisbursementRequeryUserId}`, null, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              reject(error)
            }
          })
      })
    },
    DeactivateFundRespostUser ({ rootState }, { headers, data: { fundDisbursementRequeryUserId, comment } }) {
      return new Promise((resolve, reject) => {
        return api.post(`/User/DeactivateFundRespostUser?FundDisbursementRequeryUserId=${fundDisbursementRequeryUserId}&DeactivationComment=${comment}`, null, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              reject(error)
            }
          })
      })
    },
    ActivateFundRespostUser ({ rootState }, { headers, data: { fundDisbursementRequeryUserId, comment } }) {
      return new Promise((resolve, reject) => {
        return api.post(`/User/ActivateFundRespostUser?FundDisbursementRequeryUserId=${fundDisbursementRequeryUserId}&DeactivationComment=${comment}`, null, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              reject(error)
            }
          })
      })
    },

    // -----------------------------------------------
    GetFundDisbursementRequeryUserAll ({ rootState }, config) {
      return new Promise((resolve, reject) => {
        return api.get('/User/GetFundDisbursementRequeryUserAll', config)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              reject(error)
            }
          })
      })
    },
    GetFundDisbursementRequeryUserPending ({ rootState }, config) {
      return new Promise((resolve, reject) => {
        return api.get('/User/GetFundDisbursementRequeryUserPending', config)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              reject(error)
            }
          })
      })
    },
    GetFundDisbursementRequeryUserApproved ({ rootState }, config) {
      return new Promise((resolve, reject) => {
        return api.get('/User/GetFundDisbursementRequeryUserApproved', config)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              reject(error)
            }
          })
      })
    },
    GetFundDisbursementRequeryUserDeactivated ({ rootState }, config) {
      return new Promise((resolve, reject) => {
        return api.get('/User/GetFundDisbursementRequeryUserDeactivated', config)
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', config)
            } else {
              reject(error)
            }
          })
      })
    },
    GetFundDisbursementRequeryUserById ({ rootState }, { headers, userId }) {
      return new Promise((resolve, reject) => {
        return api.get(`/User/GetFundDisbursementRequeryUserById?Id=${userId}`, { headers })
          .then(response => {
            if (response.responseCode === '04') {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              resolve(response.data)
            }
          })
          .catch(error => {
            if (error && error.response?.status === 401) {
              store.dispatch('users/UnauthorizedUser', { headers })
            } else {
              reject(error)
            }
          })
      })
    }
    // ------------------------------------------------
  }
}

export default users
